/**
 * Analytics Service (Simplified)
 *
 * Uses the simplified tracking tables:
 *   - lesson_progress: Track lesson start/completion with time
 *   - page_views: Track user journey through pages
 *   - user_events: Track interactions and clicks
 */

import { supabase } from '@/integrations/supabase/client';

export interface UserAnalytics {
  userId: string;
  totalLessonsStarted: number;
  totalLessonsCompleted: number;
  totalTimeSpentSeconds: number;
  lastActive?: Date;
  streakDays: number;
}

export interface LeaderboardEntry {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  totalLessonsCompleted: number;
  totalTimeSpentSeconds: number;
  lastActive?: Date;
  rank?: number;
}

export interface DashboardStats {
  totalUsers: number;
  activeUsersToday: number;
  activeUsersThisWeek: number;
  totalLessonsCompleted: number;
  totalTimeSpent: number;
  averageCompletionRate: number;
}

export class AnalyticsService {
  /**
   * Get analytics for the current user
   */
  async getUserAnalytics(userId?: string): Promise<UserAnalytics | null> {
    const effectiveUserId = userId || (await supabase.auth.getUser()).data.user?.id;

    if (!effectiveUserId) {
      return null;
    }

    // Get lesson progress
    const { data: progress, error } = await supabase
      .from('lesson_progress')
      .select('status, time_spent_seconds, updated_at')
      .eq('user_id', effectiveUserId);

    if (error) {
      console.error('Failed to fetch user analytics:', error);
      return null;
    }

    const lessons = progress || [];
    const completed = lessons.filter(l => l.status === 'completed').length;
    const totalTime = lessons.reduce((sum, l) => sum + (l.time_spent_seconds || 0), 0);

    // Get last activity
    const lastActivityTimestamp = lessons
      .map(l => l.updated_at ? new Date(l.updated_at).getTime() : 0)
      .sort((a, b) => b - a)[0];

    return {
      userId: effectiveUserId,
      totalLessonsStarted: lessons.length,
      totalLessonsCompleted: completed,
      totalTimeSpentSeconds: totalTime,
      lastActive: lastActivityTimestamp ? new Date(lastActivityTimestamp) : undefined,
      streakDays: await this.calculateStreak(effectiveUserId)
    };
  }

  /**
   * Track analytics event
   * Writes to user_events table
   */
  async trackAnalyticsEvent(
    eventType: string,
    eventName: string,
    eventData?: Record<string, any>
  ): Promise<void> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();

    if (authError || !user) {
      console.warn('[AnalyticsService] Cannot track event - user not authenticated');
      return;
    }

    try {
      const { error } = await supabase
        .from('user_events')
        .insert({
          user_id: user.id,
          event_type: eventType,
          event_name: eventName,
          event_data: eventData || {},
          page_path: typeof window !== 'undefined' ? window.location.pathname : undefined
        });

      if (error) {
        console.error('[AnalyticsService] Failed to track event:', error);
      } else {
        console.log(`[AnalyticsService] Event tracked: ${eventType}/${eventName}`);
      }
    } catch (err) {
      console.error('[AnalyticsService] Error tracking event:', err);
    }
  }

  /**
   * Track page view
   */
  async trackPageView(
    pagePath: string,
    pageTitle?: string,
    referrer?: string
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return;
    }

    try {
      await supabase.from('page_views').insert({
        user_id: user.id,
        page_path: pagePath,
        page_title: pageTitle,
        referrer: referrer || (typeof document !== 'undefined' ? document.referrer : null)
      });
    } catch (err) {
      console.warn('[AnalyticsService] Failed to track page view:', err);
    }
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Get total users
    const { count: totalUsers } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });

    // Get active users today (from page_views and user_events)
    const [todayViews, todayEvents] = await Promise.all([
      supabase
        .from('page_views')
        .select('user_id')
        .gte('created_at', today.toISOString()),
      supabase
        .from('user_events')
        .select('user_id')
        .gte('created_at', today.toISOString())
    ]);

    const todayUserIds = new Set([
      ...(todayViews.data || []).map(r => r.user_id),
      ...(todayEvents.data || []).map(r => r.user_id)
    ]);

    // Get active users this week
    const [weekViews, weekEvents] = await Promise.all([
      supabase
        .from('page_views')
        .select('user_id')
        .gte('created_at', weekAgo.toISOString()),
      supabase
        .from('user_events')
        .select('user_id')
        .gte('created_at', weekAgo.toISOString())
    ]);

    const weekUserIds = new Set([
      ...(weekViews.data || []).map(r => r.user_id),
      ...(weekEvents.data || []).map(r => r.user_id)
    ]);

    // Get lesson stats
    const { data: lessonStats } = await supabase
      .from('lesson_progress')
      .select('status, time_spent_seconds');

    const lessons = lessonStats || [];
    const completed = lessons.filter(l => l.status === 'completed').length;
    const totalTime = lessons.reduce((sum, l) => sum + (l.time_spent_seconds || 0), 0);

    const total = totalUsers || 0;

    return {
      totalUsers: total,
      activeUsersToday: todayUserIds.size,
      activeUsersThisWeek: weekUserIds.size,
      totalLessonsCompleted: completed,
      totalTimeSpent: totalTime,
      averageCompletionRate: total > 0 ? completed / total : 0
    };
  }

  /**
   * Get leaderboard (top performers by lessons completed)
   */
  async getLeaderboard(limit: number = 10): Promise<LeaderboardEntry[]> {
    // Get lesson progress grouped by user
    const { data: progress } = await supabase
      .from('lesson_progress')
      .select('user_id, status, time_spent_seconds, updated_at');

    if (!progress) return [];

    // Group by user
    const userStats = new Map<string, {
      completed: number;
      totalTime: number;
      lastActive: number;
    }>();

    progress.forEach(record => {
      const existing = userStats.get(record.user_id) || {
        completed: 0,
        totalTime: 0,
        lastActive: 0
      };

      if (record.status === 'completed') {
        existing.completed++;
      }
      existing.totalTime += record.time_spent_seconds || 0;

      const timestamp = record.updated_at ? new Date(record.updated_at).getTime() : 0;
      if (timestamp > existing.lastActive) {
        existing.lastActive = timestamp;
      }

      userStats.set(record.user_id, existing);
    });

    // Get user profiles
    const userIds = Array.from(userStats.keys());
    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, first_name, last_name, email')
      .in('user_id', userIds);

    const profileMap = new Map(
      (profiles || []).map(p => [p.user_id, p])
    );

    // Build leaderboard
    const entries: LeaderboardEntry[] = Array.from(userStats.entries())
      .map(([userId, stats]) => {
        const profile = profileMap.get(userId);
        return {
          userId,
          firstName: profile?.first_name,
          lastName: profile?.last_name,
          email: profile?.email,
          totalLessonsCompleted: stats.completed,
          totalTimeSpentSeconds: stats.totalTime,
          lastActive: stats.lastActive ? new Date(stats.lastActive) : undefined
        };
      })
      .sort((a, b) => b.totalLessonsCompleted - a.totalLessonsCompleted)
      .slice(0, limit)
      .map((entry, index) => ({ ...entry, rank: index + 1 }));

    return entries;
  }

  /**
   * Calculate streak days for user
   */
  async calculateStreak(userId?: string): Promise<number> {
    const effectiveUserId = userId || (await supabase.auth.getUser()).data.user?.id;

    if (!effectiveUserId) {
      return 0;
    }

    // Get distinct active dates from page_views and user_events
    const [viewsResult, eventsResult] = await Promise.all([
      supabase
        .from('page_views')
        .select('created_at')
        .eq('user_id', effectiveUserId)
        .order('created_at', { ascending: false }),
      supabase
        .from('user_events')
        .select('created_at')
        .eq('user_id', effectiveUserId)
        .order('created_at', { ascending: false })
    ]);

    const allDates = [
      ...(viewsResult.data || []).map(r => r.created_at),
      ...(eventsResult.data || []).map(r => r.created_at)
    ];

    if (allDates.length === 0) {
      return 0;
    }

    // Extract unique dates
    const uniqueDates = new Set(
      allDates.map(date => new Date(date).toDateString())
    );

    // Calculate streak from today
    let streak = 0;
    const today = new Date();

    for (let i = 0; i < 365; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);

      if (uniqueDates.has(checkDate.toDateString())) {
        streak++;
      } else if (i > 0) {
        // Break on first gap (after checking today)
        break;
      }
    }

    return streak;
  }

  /**
   * Format time spent for display
   */
  formatTimeSpent(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
